import dayjs from "dayjs";
import { toast } from "react-toastify";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useCallback, useState } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import Button from "../components/form/Button";
import { classNames } from "../helpers/classNames";

// Extend plugins BEFORE using .tz()
dayjs.extend(utc);
dayjs.extend(timezone);

export const handleDownload = (filePath, folderName) => {
  // Create an anchor element
  const anchor = document.createElement("a");
  // Set the href attribute to the URL of the file to download
  anchor.href = filePath; // Change "/path/to/your/sample_file.ext" to the actual path of your file
  // Set the download attribute to specify the filename
  anchor.target = "_blank";
  anchor.download = folderName; // Change "sample_file.ext" to your desired filename
  // Simulate a click on the anchor element to trigger the download
  anchor.click();
};

export const allowedExtensionsForImage = ["jpg", "jpeg", "png", "webp"];

function validateFileExtensions(allowedExtensions, files) {
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      return false;
    }
  }
  return true;
}

export const validateImage = (event) => {
  const files = event.target.files;
  const isValid = validateFileExtensions(allowedExtensionsForImage, files);
  if (!isValid) {
    toast.error("File is not valid!");
    return false;
  }
  return true;
};

export const findEmptyIndexes = (socialArray) => {
  return socialArray
    ?.map((item, index) => (!item?.url ? index : null))
    .filter((index) => index !== null);
};

export const generatePassword = ({ length = 10 }) => {
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const special = "!@#$%^&*()_+[]{}<>?,.";

  const all = upper + lower + numbers + special;

  // Ensure at least one character from each category
  const getRandom = (str) => str[Math.floor(Math.random() * str.length)];
  let password = [
    getRandom(upper),
    getRandom(lower),
    getRandom(numbers),
    getRandom(special),
  ];

  // Fill the rest of the password length
  for (let i = 4; i < length; i++) {
    password.push(getRandom(all));
  }

  // Shuffle password so fixed positions aren't predictable
  return password.sort(() => 0.5 - Math.random()).join("");
};

export const statusText = {
  Received_Under_Review: {
    title: "Received/Under Review",
    description: "Your application has been received and is under review.",
  },
  Additional_Action_Required: {
    title: "Additional Action Required",
    description:
      "Check your email or portal—more information is needed to proceed.",
  },
  Interview_Stage: {
    title: "Interview Stage",
    description:
      "Congrats! You’re shortlisted—check your email for interview details.",
  },
  Awarded: {
    title: "Awarded",
    description: "Congrats! You have been selected as a scholarship recipient.",
  },
  Draft: {
    title: "Draft",
    description: "Application is stll incomolete !",
  },
  Not_Selected: {
    title: " Not Selected",
    description:
      "Not selected this time—thank you for applying and please try again.",
  },
};

export const isDate = (value) => {
  return (
    typeof value === "string" &&
    !isNaN(Date.parse(value)) && // Checks if string is parsable
    !isNaN(new Date(value).getTime()) // Valid date object
  );
};

export const getDetailsObject = (bannerType, val) => {
  switch (bannerType) {
    case "home":
      return {
        testimonial: {
          title: val?.testimonialTitle,
          description: val?.testimonialDesc,
        },
        partner: {
          title: val?.partnerTitle,
          description: val?.partnerDesc,
        },
        existence: {
          title: val?.existenceTitle,
          count: val?.existenceCount,
        },
        scholarAward: {
          title: val?.scholarAwardTitle,
          count: val?.scholarAwardCount,
        },
        beneficiary: {
          title: val?.beneficiaryTitle,
          count: val?.beneficiaryCount,
        },
        fundDistribution: {
          title: val?.fundDistributionTitle,
          count: val?.fundDistributionCount,
        },
        videoSection: {
          title: val?.videoSectionTitle,
          link: val?.videoSectionDesc,
        },
      };

    case "apply":
      return {
        eligibility: {
          title: val?.eligibilityTitle,
          description: val?.eligibilityDesc,
          subtitle: val?.eligibilitySubTitle,
          eligibilityImage: val?.eligibilityImage,
        },
        timeline: {
          title: val?.timelineTitle,
          description: val?.timelineDesc,
          image: val?.timelineImage,
        },
        faq: {
          title: val?.faqTitle,
          description: val?.faqDesc,
          image: val?.faqImage,
        },
      };

    case "v'Peters":
      return {
        publication: {
          title: val?.publicationTitle,
          description: val?.publicationDesc,
        },
        inspire: {
          title: val?.inspireTitle,
          description: val?.inspireDesc,
          image: val?.inspireImage,
        },
        impact: {
          title: val?.impactTitle,
          description: val?.impactDesc,
          image: val?.impactImage,
        },
        firstSubSection: {
          title: val?.firstSubSectionTitle,
          count: val?.firstSubSectionCount,
          image: val?.subSectionimage,
        },
        secondSubSection: {
          title: val?.secondSubSectionTitle,
          count: val?.secondSubSectionCount,
        },
        thirdSubSection: {
          title: val?.thirdSubSectionTitle,
          count: val?.thirdSubSectionCount,
        },
      };

    case "updatesAndBlogs":
      return {
        blog: {
          title: val?.blogTitle,
          description: val?.blogDesc,
        },
      };

    case "takeAction":
      return {
        takeAction: {
          image: val?.actionImage,
          sectionImage: val?.sectionImage,
        },
      };

    default:
      return {};
  }
};

export const forceDownload = async (url, filename = "download") => {
  try {
    const response = await fetch(url, { mode: "cors" }); // Must support CORS
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed:", error);
    alert("Failed to download the file.");
  }
};

export function truncateHtmlByWords(html = "", wordLimit = 125) {
  if (!html) return "-";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  let wordCount = 0;
  const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT);
  const collected = [];

  while (walker.nextNode()) {
    const nodeText = walker.currentNode.nodeValue;
    const words = nodeText.trim().split(/\s+/).filter(Boolean);
    const remaining = wordLimit - wordCount;

    if (remaining <= 0) break;

    if (words.length <= remaining) {
      collected.push(nodeText);
      wordCount += words.length;
    } else {
      collected.push(words.slice(0, remaining).join(" ") + "...");
      wordCount += remaining;
      break;
    }
  }

  const outputDiv = document.createElement("div");
  outputDiv.textContent = collected.join(" ");
  return outputDiv.innerHTML;
}

export const isEqual = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

export const taxTypes = [
  { name: "Sales tax", value: "Sales_Tax" },
  { name: "VAT", value: "VAT" },
  { name: "GST", value: "GST" },
];

export const includeTaxOptions = [
  { name: "No (exclusive)", value: "exclusive" },
  { name: "Yes (inclusive)", value: "inclusive" },
];

export const scopeOptions = [
  { name: "All", value: "all" },
  { name: "Plan", value: "plan" },
  { name: "Addon", value: "addon" },
];

export const formatEventDate = (
  startDateStr,
  endDateStr,
  timezone = null,
  options = {}
) => {
  // Default options
  const {
    dateFormat = "full", // 'full', 'short', 'medium'
    timeFormat = "12h", // '12h', '24h'
    showTimezone = false,
    separator = "•",
    includeDayOfWeek = true,
  } = options;

  if (!startDateStr) return "";

  const targetTimezone =
    timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  try {
    const start = dayjs(startDateStr).tz(targetTimezone);
    const end = endDateStr ? dayjs(endDateStr).tz(targetTimezone) : null;

    // Validate dates
    if (!start.isValid() || (end && !end.isValid())) {
      return "Invalid date";
    }

    // Date format options
    const dateFormats = {
      full: includeDayOfWeek ? "dddd, MMMM D, YYYY" : "MMMM D, YYYY",
      medium: includeDayOfWeek ? "ddd, MMM D, YYYY" : "MMM D, YYYY",
      short: "MM/DD/YYYY",
    };

    // Time format options
    const timeFormats = {
      "12h": "h:mm A",
      "24h": "HH:mm",
    };

    const selectedDateFormat = dateFormats[dateFormat] || dateFormats.medium;
    const selectedTimeFormat = timeFormats[timeFormat] || timeFormats["12h"];

    // Format date part
    let datePart = start.format(selectedDateFormat);

    // Format time part
    let timePart = "";

    if (end && start.isSame(end, "day")) {
      // Same day event with end time
      timePart = `${start.format(selectedTimeFormat)} – ${end.format(
        selectedTimeFormat
      )}`;
    } else if (end && !start.isSame(end, "day")) {
      // Multi-day event
      const endDatePart = end.format(selectedDateFormat);
      const startTime = start.format(selectedTimeFormat);
      const endTime = end.format(selectedTimeFormat);

      return `${datePart}, ${startTime} – ${endDatePart}, ${endTime}${
        showTimezone ? ` ${start.format("z")}` : ""
      }`;
    } else {
      // Single point in time or no end date
      timePart = start.format(selectedTimeFormat);
    }

    // Add timezone if requested
    const timezoneStr = showTimezone ? ` ${start.format("z")}` : "";

    // Professional formatting with proper separator
    return `${datePart} ${separator} ${timePart}${timezoneStr}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid date format";
  }
};

export const useClipboard = (resetDelay = 2000) => {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(
    (text) => {
      if (!navigator?.clipboard) {
        console.warn("Clipboard API not supported.");
        return;
      }

      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          const timeout = setTimeout(() => setCopied(false), resetDelay);

          return () => clearTimeout(timeout);
        })
        .catch((err) => {
          console.error("Copy failed", err);
        });
    },
    [resetDelay]
  );

  return { copied, copy };
};

export const ShareButtons = ({
  url,
  text = "Check this out!",
  buttonIcon,
  buttonClass,
  buttonIconClass,
  buttonLabelClasses,
  buttonLabel,
  style,
  anchor = "bottom end", // New anchor positioning
  gap = 2, // Gap between button and panel
  panelWidth = "w-86", // Configurable width
  size = "lg", // sm, md, lg, xl
  showCopyLink = false, // Show copy link option
  showEmail = true, // Show email option
  showNativeShare = true, // Show native share option
  title = "Share this content", // Custom title
  subtitle = "Choose your preferred platform", // Custom subtitle
  copySuccessMessage = "Link copied to clipboard!", // Custom copy message
  platforms = null, // Custom platforms array
  onShare = () => {}, // Callback when sharing
  onCopy = () => {}, // Callback when copying
  className = "", // Additional wrapper classes
  disabled = false, // Disable the button
  loading = false, // Loading state
}) => {
  const encodedURL = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);
  // Size configurations
  const sizeClasses = {
    sm: {
      button: "w-8 h-8 text-xs",
      icon: "w-8 h-8 text-sm",
      grid: "grid-cols-4",
      text: "text-xs",
      spacing: "gap-2",
      padding: "p-3",
    },
    md: {
      button: "w-10 h-10 text-sm",
      icon: "w-10 h-10 text-base",
      grid: "grid-cols-3",
      text: "text-xs",
      spacing: "gap-3",
      padding: "p-4",
    },
    lg: {
      button: "w-12 h-12 text-base",
      icon: "w-12 h-12 text-lg",
      grid: "grid-cols-3",
      text: "text-sm",
      spacing: "gap-2",
      padding: "p-4",
    },
    xl: {
      button: "w-14 h-14 text-lg",
      icon: "w-14 h-14 text-xl",
      grid: "grid-cols-2",
      text: "text-base",
      spacing: "gap-2",
      padding: "p-6",
    },
  };

  const currentSize = sizeClasses[size] || sizeClasses.md;

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedURL}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedURL}`,
    telegram: `https://t.me/share/url?url=${encodedURL}&text=${encodedText}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedURL}&text=${encodedText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedURL}`,
    reddit: `https://www.reddit.com/submit?url=${encodedURL}&title=${encodedText}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedURL}&description=${encodedText}`,
  };

  const defaultPlatforms = [
    {
      name: "Facebook",
      icon: "fa-brands fa-facebook-f",
      link: shareLinks.facebook,
      color: "bg-[#1877F2] hover:bg-[#166fe5]",
    },
    {
      name: "WhatsApp",
      icon: "fa-brands fa-whatsapp",
      link: shareLinks.whatsapp,
      color: "bg-[#25D366] hover:bg-[#22c55e]",
    },
    {
      name: "Telegram",
      icon: "fa-brands fa-telegram",
      link: shareLinks.telegram,
      color: "bg-[#0088cc] hover:bg-[#0ea5e9]",
    },
    {
      name: "Twitter",
      icon: "fa-brands fa-x-twitter",
      link: shareLinks.twitter,
      color: "bg-black hover:bg-gray-800",
    },
    {
      name: "LinkedIn",
      icon: "fa-brands fa-linkedin-in",
      link: shareLinks.linkedin,
      color: "bg-[#0077b5] hover:bg-[#0284c7]",
    },
    {
      name: "Reddit",
      icon: "fa-brands fa-reddit-alien",
      link: shareLinks.reddit,
      color: "bg-[#FF4500] hover:bg-[#f97316]",
    },
    {
      name: "Pinterest",
      icon: "fa-brands fa-pinterest-p",
      link: shareLinks.pinterest,
      color: "bg-[#E60023] hover:bg-[#dc2626]",
    },
  ];

  const selectedPlatforms = platforms || defaultPlatforms;

  const handleNativeShare = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      if (navigator.share) {
        await navigator.share({ title: "Share", text, url });
        onShare("native", { text, url });
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(`${text} ${url}`);
        alert(copySuccessMessage);
        onCopy(url);
      }
    } catch (err) {
      console.error("Native sharing failed:", err);
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(`${text} ${url}`);
        alert(copySuccessMessage);
        onCopy(url);
      } catch (clipboardErr) {
        console.error("Clipboard failed:", clipboardErr);
        alert("Your browser doesn't support sharing or clipboard access.");
      }
    }
  };

  const handleCopyLink = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      await navigator.clipboard.writeText(url);
      alert(copySuccessMessage);
      onCopy(url);
    } catch (err) {
      console.error("Copy failed:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
        alert(copySuccessMessage);
        onCopy(url);
      } catch (fallbackErr) {
        console.error("Fallback copy failed:", fallbackErr);
        alert("Unable to copy link");
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  const handlePlatformShare = (platform, link, close) => {
    onShare(platform.name.toLowerCase(), { platform, link, text, url });
    // Close popover after a short delay for better UX
    setTimeout(close, 100);
  };

  // Build additional options array
  const additionalOptions = [];

  if (showCopyLink) {
    additionalOptions.push({
      name: "Copy Link",
      icon: "fa-solid fa-link",
      color: "bg-gray-600 hover:bg-gray-700",
      action: handleCopyLink,
    });
  }

  if (showNativeShare) {
    additionalOptions.push({
      name: "More Options",
      icon: "fa-solid fa-share-nodes",
      color:
        "bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600",
      action: handleNativeShare,
    });
  }

  if (showEmail) {
    additionalOptions.push({
      name: "Email",
      icon: "fa-solid fa-envelope",
      color: "bg-red-500 hover:bg-red-600",
      link: `mailto:?subject=${encodedText}&body=${encodedText}%20${encodedURL}`,
    });
  }

  const allOptions = [...selectedPlatforms, ...additionalOptions];

  if (loading) {
    return (
      <div className={classNames("relative", className)}>
        <button
          className={classNames(
            "bg-transparent border border-slate-200 text-slate-400 cursor-not-allowed flex items-center justify-center gap-2",
            currentSize.button,
            buttonClass
          )}
          disabled={true}
        >
          <i
            className={classNames(
              "fa-duotone fa-spinner-third animate-spin",
              buttonIconClass
            )}
          ></i>
          {buttonLabel && (
            <span className={buttonLabelClasses}>Loading...</span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className={classNames("relative", className)}>
      <Popover className="relative">
        {/* Toggle Button */}
        <PopoverButton
          className={classNames(
            "bg-transparent border border-slate-200 text-deep-purple hover:bg-gray-50 hover:border-primary transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none flex items-center justify-center gap-2",
            currentSize.button,
            disabled &&
              "opacity-50 cursor-not-allowed hover:bg-transparent hover:border-slate-200",
            buttonClass
          )}
          disabled={disabled}
          style={style}
        >
          <i
            className={classNames(buttonIcon || "fa fa-share", buttonIconClass)}
          ></i>
          {buttonLabel && (
            <span className={buttonLabelClasses}>{buttonLabel}</span>
          )}
        </PopoverButton>

        {/* Popover Panel */}
        <PopoverPanel
          anchor={anchor}
          gap={gap}
          modal={false}
          transition
          className={classNames(
            "z-50 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden transition duration-200 ease-out",
            "data-[closed]:scale-95 data-[closed]:opacity-0 data-[closed]:translate-y-1",
            "data-[enter]:duration-200 data-[leave]:duration-150",
            "data-[enter]:ease-out data-[leave]:ease-in",
            panelWidth
          )}
        >
          {({ close }) => (
            <>
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-100 via-purple-50 to-purple-200 border-b border-slate-200 shadow-sm">
                <div className="flex items-center justify-between px-5 py-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800 text-base leading-snug">
                      {title}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">{subtitle}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      close();
                    }}
                    className="text-slate-400 hover:text-slate-600 rounded-full hover:bg-white/70 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 ml-3 shadow-sm bg-white w-8 aspect-square flex items-center justify-center"
                    aria-label="Close share menu"
                  >
                    <i className="fa-solid fa-xmark text-lg"></i>
                  </button>
                </div>
              </div>

              {/* Social Icons Grid */}
              <div className={currentSize.padding}>
                <div
                  className={classNames(
                    "grid",
                    currentSize.grid,
                    currentSize.spacing
                  )}
                >
                  {allOptions.map(({ name, icon, link, color, action }) => {
                    const handleClick = (e) => {
                      e.stopPropagation();
                      if (action) {
                        action(e);
                      } else if (link) {
                        handlePlatformShare(
                          { name, icon, link, color },
                          link,
                          close
                        );
                      }
                    };

                    const content = (
                      <div className="flex flex-col items-center space-y-2 group">
                        <div
                          className={classNames(
                            "flex items-center justify-center rounded-full text-white transition-all duration-200 group-hover:scale-105 group-hover:shadow-lg group-focus-within:scale-105 group-focus-within:shadow-lg ",
                            currentSize.icon,
                            color
                          )}
                        >
                          <i className={classNames(icon, currentSize.text)}></i>
                        </div>
                        <span
                          className={classNames(
                            "text-slate-700 font-medium group-hover:text-slate-900 transition-colors text-center leading-tight max-w-full truncate",
                            currentSize.text
                          )}
                        >
                          {name}
                        </span>
                      </div>
                    );

                    if (action) {
                      return (
                        <button
                          key={name}
                          onClick={handleClick}
                          title={name}
                          aria-label={name}
                          className="focus:outline-none focus:ring-2 focus:ring-primary/30 rounded-lg p-1 transition-transform duration-200 hover:scale-105"
                        >
                          {content}
                        </button>
                      );
                    }

                    return (
                      <a
                        key={name}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Share on ${name}`}
                        aria-label={`Share on ${name}`}
                        onClick={handleClick}
                        className="focus:outline-none focus:ring-2 focus:ring-primary/30 rounded-lg p-1 transition-transform duration-200 hover:scale-105"
                      >
                        {content}
                      </a>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </PopoverPanel>
      </Popover>
    </div>
  );
};
